import { useState, useEffect } from 'react';
import { adminGetBanners, adminCreateBanner, adminUpdateBanner, adminDeleteBanner } from '../../utils/api';
import ImageUploader from '../../components/common/ImageUploader';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const EMPTY = {
  title: '', subtitle: '', buttonText: 'Shop Now', buttonLink: '/products',
  image: '', bgColor: 'linear-gradient(135deg,#006A4E 0%,#00875f 100%)',
  textColor: '#ffffff', isActive: true, sortOrder: 0,
};

const PRESET_GRADIENTS = [
  'linear-gradient(135deg,#006A4E 0%,#00875f 100%)',
  'linear-gradient(135deg,#E8192C 0%,#c01424 100%)',
  'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)',
  'linear-gradient(135deg,#7c3aed 0%,#a855f7 100%)',
  'linear-gradient(135deg,#d97706 0%,#f59e0b 100%)',
  'linear-gradient(135deg,#0f1923 0%,#374151 100%)',
];

export default function AdminBanners() {
  const [banners, setBanners]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);

  const fetch = () => adminGetBanners().then(r => setBanners(r.data)).finally(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditBanner(null); setShowModal(true); };
  const openEdit   = b  => { setForm({ ...b }); setEditBanner(b); setShowModal(true); };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editBanner) { await adminUpdateBanner(editBanner._id, form); toast.success('Banner updated!'); }
      else             { await adminCreateBanner(form);                  toast.success('Banner created!'); }
      setShowModal(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete banner "${title}"?`)) return;
    try { await adminDeleteBanner(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const toggleActive = async b => {
    try { await adminUpdateBanner(b._id, { isActive: !b.isActive }); fetch(); }
    catch { toast.error('Failed'); }
  };

  const f = field => e => setForm(p => ({ ...p, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Homepage Banners</h2>
          <p className={styles.pageSubtitle}>{banners.length} banners — editable from here, shown on homepage</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={15}/> Add Banner</button>
      </div>

      {/* Banner preview cards */}
      {loading ? <div className="spinner-center"><div className="spinner"/></div> : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {banners.length === 0 && (
            <div style={{ textAlign:'center', padding:'3rem', color:'var(--text-muted)' }}>
              No banners yet. <button className="btn btn-primary btn-sm" onClick={openCreate} style={{ marginLeft:8 }}>Create First Banner</button>
            </div>
          )}
          {banners.map(b => (
            <div key={b._id} style={{ display:'flex', gap:16, alignItems:'center', background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden', opacity: b.isActive ? 1 : 0.5 }}>
              {/* Mini preview */}
              <div style={{ width:160, height:80, background:b.bgColor, flex:'shrink 0', display:'flex', alignItems:'center', justifyContent:'center', padding:12, flexShrink:0, position:'relative' }}>
                {b.image && <img src={b.image} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', opacity:.25 }}/>}
                <p style={{ color:b.textColor||'#fff', fontWeight:800, fontSize:'.75rem', textAlign:'center', position:'relative', zIndex:1 }}>{b.title}</p>
              </div>
              <div style={{ flex:1, padding:'12px 0', minWidth:0 }}>
                <p style={{ fontWeight:700, fontSize:'.9rem' }}>{b.title}</p>
                <p style={{ fontSize:'.78rem', color:'var(--text-secondary)', marginTop:2 }}>{b.subtitle}</p>
                <p style={{ fontSize:'.72rem', color:'var(--primary)', marginTop:4 }}>→ {b.buttonLink} · Sort: {b.sortOrder}</p>
              </div>
              <div style={{ display:'flex', gap:6, padding:'0 16px', flexShrink:0 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(b)} title={b.isActive ? 'Hide' : 'Show'}>
                  {b.isActive ? <FiEye size={14} color="var(--success)"/> : <FiEyeOff size={14} color="var(--text-muted)"/>}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(b)}><FiEdit2 size={14}/></button>
                <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => handleDelete(b._id, b.title)}><FiTrash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editBanner ? 'Edit Banner' : 'Create Banner'}</h3>
              <button className={styles.modalClose} onClick={() => setShowModal(false)}><FiX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div className={styles.formGrid2}>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Title *</label>
                  <input className="form-input" value={form.title} onChange={f('title')} placeholder="ঈদ স্পেশাল সেল 🎉" required/>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Subtitle</label>
                  <input className="form-input" value={form.subtitle} onChange={f('subtitle')} placeholder="Up to 50% off on electronics…"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Button Text</label>
                  <input className="form-input" value={form.buttonText} onChange={f('buttonText')} placeholder="Shop Now"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Button Link</label>
                  <input className="form-input" value={form.buttonLink} onChange={f('buttonLink')} placeholder="/products?featured=true"/>
                </div>

                {/* Background color presets */}
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Background Gradient</label>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                    {PRESET_GRADIENTS.map(g => (
                      <button key={g} type="button" onClick={() => setForm(p => ({ ...p, bgColor: g }))}
                        style={{ width:36, height:36, background:g, borderRadius:8, border:`3px solid ${form.bgColor===g?'var(--primary)':'transparent'}`, cursor:'pointer' }}/>
                    ))}
                  </div>
                  <input className="form-input" value={form.bgColor} onChange={f('bgColor')} placeholder="linear-gradient(…)"/>
                </div>

                {/* Preview */}
                <div style={{ gridColumn:'1/-1', height:80, background:form.bgColor, borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--border)' }}>
                  <p style={{ color:form.textColor||'#fff', fontWeight:800, fontSize:'1rem' }}>{form.title || 'Banner Preview'}</p>
                </div>

                {/* Image uploader */}
                <div style={{ gridColumn:'1/-1' }}>
                  <ImageUploader label="Banner Image (optional)" value={form.image} onChange={url => setForm(p => ({ ...p, image: url }))}/>
                </div>

                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-input" value={form.sortOrder} onChange={f('sortOrder')} min={0}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Text Color</label>
                  <input type="color" className="form-input" value={form.textColor} onChange={f('textColor')} style={{ height:42 }}/>
                </div>
                <label className={styles.checkbox} style={{ gridColumn:'1/-1' }}>
                  <input type="checkbox" checked={form.isActive} onChange={f('isActive')}/> Active (visible on homepage)
                </label>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editBanner ? 'Update Banner' : 'Create Banner'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
